import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { toast } from 'react-hot-toast';
import supabase from '../../../config/supabaseClient';
import { 
  primaryButtonStyle, 
  secondaryButtonStyle, 
  dangerButtonStyle, 
  applyDisabledStyle 
} from '../utils/buttonStyles';

/**
 * Componente para cadastro e gerenciamento de localidades
 */
const CadastroLocalidade = () => {
  const { 
    localidades, 
    carregarLocalidades, 
    salvarLocalidade, 
    loading 
  } = useFossasSepticas();

  const [showForm, setShowForm] = useState(false);
  const [editingLocalidade, setEditingLocalidade] = useState(null);
  const [formData, setFormData] = useState({
    descricao: '',
    latitude: null,
    longitude: null
  });
  const [errors, setErrors] = useState({});

  // Carregar localidades ao montar o componente
  useEffect(() => {
    carregarLocalidades();
  }, [carregarLocalidades]);

  // Abrir formulário para nova localidade
  const handleNovaLocalidade = () => {
    setFormData({
      descricao: '',
      latitude: null,
      longitude: null
    });
    setEditingLocalidade(null);
    setShowForm(true);
    setErrors({});
  };

  // Abrir formulário para editar localidade
  const handleEditar = (localidade) => {
    setFormData({
      id: localidade.id,
      descricao: localidade.descricao,
      latitude: localidade.latitude,
      longitude: localidade.longitude
    });
    setEditingLocalidade(localidade);
    setShowForm(true);
    setErrors({});
  };

  // Cancelar edição
  const handleCancelar = () => {
    setShowForm(false);
    setEditingLocalidade(null);
  };

  // Atualizar estado do formulário
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let processedValue = value;
    
    // Converter valores numéricos
    if (type === 'number') {
      processedValue = value === '' ? null : parseFloat(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const success = await salvarLocalidade(formData);
      
      if (success) {
        setShowForm(false);
        setEditingLocalidade(null);
      }
    }
  };

  // Excluir localidade
  const handleExcluir = async (localidadeId) => {
    if (window.confirm('Tem certeza que deseja excluir esta localidade? Esta ação não pode ser desfeita.')) {
      try {
        const { error } = await supabase
          .from('ccbairros')
          .delete()
          .eq('id', localidadeId);
        
        if (error) throw error;
        
        toast.success('Localidade excluída com sucesso');
        carregarLocalidades();
      } catch (error) {
        console.error('Erro ao excluir localidade:', error);
        toast.error('Erro ao excluir localidade');
      }
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Gerenciador de Bairros</h2>
        <button 
          onClick={handleNovaLocalidade}
          style={primaryButtonStyle}
        >
          <Plus size={16} />
          Novo Bairro
        </button>
      </div>
      
      {showForm && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px' }}>{editingLocalidade ? 'Editar Bairro' : 'Novo Bairro'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Descrição</label>
                <input
                  type="text"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    borderColor: errors.descricao ? '#dc3545' : null 
                  }}
                  placeholder="Nome do bairro/localidade"
                />
                {errors.descricao && <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px' }}>{errors.descricao}</div>}
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Latitude (opcional)</label>
                <input
                  type="number"
                  name="latitude"
                  value={formData.latitude || ''}
                  onChange={handleChange}
                  step="any"
                  style={{ width: '100%' }}
                  placeholder="Ex: -20.359292"
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Longitude (opcional)</label>
                <input
                  type="number"
                  name="longitude"
                  value={formData.longitude || ''}
                  onChange={handleChange}
                  step="any"
                  style={{ width: '100%' }}
                  placeholder="Ex: -41.247282"
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                type="button" 
                onClick={handleCancelar}
                style={secondaryButtonStyle}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                disabled={loading.localidades}
                style={applyDisabledStyle(primaryButtonStyle, loading.localidades)}
              >
                {loading.localidades ? 'Salvando...' : editingLocalidade ? 'Atualizar' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Estatísticas rápidas */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '15px',
        marginBottom: '25px'
      }}>
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          border: '1px solid #d6d8db',
          borderLeft: '5px solid #6c757d'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#383d41', fontSize: '16px' }}>Total de Bairros</h3>
          <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
            {localidades.length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Bairros cadastrados</div>
        </div>
        
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          border: '1px solid #c3e6cb',
          borderLeft: '5px solid #28a745'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#155724', fontSize: '16px' }}>Com Coordenadas</h3>
          <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
            {localidades.filter(l => l.latitude && l.longitude).length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Bairros com localização</div>
        </div>
        
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          border: '1px solid #b8daff',
          borderLeft: '5px solid #007bff'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#004085', fontSize: '16px' }}>Sem Coordenadas</h3>
          <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
            {localidades.filter(l => !l.latitude || !l.longitude).length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Bairros sem localização</div>
        </div>
      </div>

      {loading.localidades && !showForm ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ 
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderLeftColor: '#2e7d32',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p>Carregando localidades...</p>
        </div>
      ) : (
        <>
          {localidades.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <p>Nenhum bairro cadastrado.</p>
              <button 
                onClick={handleNovaLocalidade}
                style={{
                  ...primaryButtonStyle,
                  margin: '0 auto'
                }}
              >
                <Plus size={16} />
                Cadastrar Primeiro Bairro
              </button>
            </div>
          ) : (
            <div className="propriedades-grid">
              {localidades.map(localidade => (
                <div key={localidade.id} className="propriedade-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>{localidade.descricao}</h3>
                    <div style={{ 
                      backgroundColor: localidade.latitude && localidade.longitude ? '#d4edda' : '#f8d7da',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: localidade.latitude && localidade.longitude ? '#155724' : '#721c24'
                    }}>
                      {localidade.latitude && localidade.longitude ? 'Com Coordenadas' : 'Sem Coordenadas'}
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', fontSize: '14px', marginBottom: '15px' }}>
                    {localidade.latitude && localidade.longitude && (
                      <div>
                        <strong>Coordenadas:</strong> {localidade.latitude.toFixed(6)}, {localidade.longitude.toFixed(6)}
                      </div>
                    )}
                    <div>
                      <strong>Propriedades:</strong> {localidade.propriedades?.length || 0}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                    <button 
                      onClick={() => handleEditar(localidade)}
                      style={{ 
                        ...secondaryButtonStyle,
                        flex: 1,
                        justifyContent: 'center'
                      }}
                    >
                      <Edit size={16} />
                      Editar
                    </button>
                    <button 
                      onClick={() => handleExcluir(localidade.id)}
                      style={{ 
                        ...dangerButtonStyle,
                        flex: 1,
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 size={16} />
                      Excluir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .propriedades-grid {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        
        .propriedade-card {
          background-color: white;
          border-radius: 8px;
          padding: 15px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default CadastroLocalidade;